import React from 'react';
import { Avatar } from './Avatar';
import type { IframeBlock as IframeBlockType } from '@/types';

interface IframeMessageProps {
    message: IframeBlockType;
    botAvatar: string;
    customStyles?: {
        primaryColor?: string;
        secondaryColor?: string;
        botMessageBg?: string;
        botMessageText?: string;
        borderRadius?: number;
        fontSize?: number;
    };
}

/**
 * Sanitise an iframe src URL.
 * Only https: (and protocol-relative //) URLs are allowed.
 * Returns empty string for anything suspicious (javascript:, data:, etc.)
 */
function sanitizeUrl(raw: string): string {
    if (!raw) return '';
    const trimmed = raw.trim();
    if (trimmed.startsWith('https://') || trimmed.startsWith('//')) {
        return trimmed;
    }
    return '';
}

export const IframeMessage: React.FC<IframeMessageProps> = ({ message, botAvatar, customStyles }) => {
    const botBgColor = customStyles?.botMessageBg || customStyles?.secondaryColor || '#ffffff';
    const botTextColor = customStyles?.botMessageText ?? '#1f2937';
    const borderRadius = customStyles?.borderRadius || 16;
    const fontSize = customStyles?.fontSize ?? 14;
    const height = message.height || 315;
    const safeSrc = sanitizeUrl(message.url);

    return (
        <div className="zentri-flex zentri-items-start zentri-gap-3 zentri-mb-4 zentri-group zentri-animate-fade-in">
            <Avatar src={botAvatar} alt="Bot" size="md" />

            <div
                className="zentri-max-w-lg zentri-w-full zentri-transition-all zentri-duration-300 zentri-ease-out hover:zentri-shadow-elegant zentri-border zentri-border-neutral-100 zentri-overflow-hidden"
                style={{
                    backgroundColor: botBgColor,
                    borderRadius: `${borderRadius}px`,
                    borderTopLeftRadius: '0',
                    borderBottomRightRadius: `${borderRadius}px`,
                }}
            >
                {/* Optional title */}
                {message.title && (
                    <div
                        className="zentri-px-3.5 zentri-py-2"
                        style={{ color: botTextColor, fontSize: `${fontSize}px` }}
                    >
                        <p className="zentri-font-medium">{message.title}</p>
                    </div>
                )}

                {/* Iframe */}
                {safeSrc ? (
                    <iframe
                        src={safeSrc}
                        title={message.title || 'Embedded content'}
                        width="100%"
                        height={height}
                        style={{ border: 0, display: 'block' }}
                        allowFullScreen={message.allowFullscreen ?? true}
                        loading="lazy"
                    />
                ) : (
                    <div
                        className="zentri-px-3.5 zentri-py-4 zentri-text-center zentri-text-sm zentri-opacity-50"
                        style={{ color: botTextColor }}
                    >
                        Invalid or missing embed URL
                    </div>
                )}
            </div>
        </div>
    );
};
